import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  MapPin,
  NavigationArrow,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const NEXT = { Pending: "On site", "On site": "Done", Done: "Pending" };
const STYLE = {
  Pending: "border-zinc-700 bg-zinc-950 text-zinc-300",
  "On site": "border-yellow-500 bg-yellow-500/15 text-yellow-300",
  Done: "border-emerald-500 bg-emerald-500/15 text-emerald-300",
};
const KIND_STYLE = {
  Template: "border-purple-500/30 text-purple-300",
  Fabrication: "border-yellow-500/30 text-yellow-300",
  Install: "border-blue-500/30 text-blue-300",
  Service: "border-red-500/30 text-red-300",
};
const ORDER = { Template: 0, Fabrication: 1, Install: 2, Service: 3 };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function MyDay() {
  const { user } = useAuth();
  const [stops, setStops] = useState([]);
  const [mineOnly, setMineOnly] = useState(true);
  const [tasks, setTasks] = useState([]);
  const date = todayIso();

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs/schedule", {
      params: { month: date.slice(0, 7) },
    });
    api
      .get("/tasks", { params: { date, carry: true } })
      .then((r) => setTasks(r.data));
    setStops(
      data.events
        .filter((e) => e.date === date)
        .sort((a, b) => (ORDER[a.kind] ?? 9) - (ORDER[b.kind] ?? 9)),
    );
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const me = user?.name || user?.email;
  const visible = mineOnly
    ? stops.filter((s) => s.crew && s.crew === me)
    : stops;
  const done = visible.filter((s) => s.stop_status?.status === "Done").length;

  const myTasks = tasks.filter((t) =>
    mineOnly ? t.assignee === me || !t.assignee : true,
  );

  const toggleTask = async (t) => {
    await api.patch(`/tasks/${t.id}/done`, { done: !t.done });
    setTasks((list) =>
      list.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)),
    );
  };

  const cycle = async (stop) => {
    const current = stop.stop_status?.status || "Pending";
    const next = NEXT[current];
    const url =
      stop.entity === "service"
        ? `/service-calls/${stop.job_id}/status`
        : `/jobs/${stop.job_id}/stops/${stop.kind}`;
    try {
      await api.patch(url, { status: next });
      setStops((list) =>
        list.map((s) =>
          s.job_id === stop.job_id && s.kind === stop.kind
            ? { ...s, stop_status: { ...s.stop_status, status: next } }
            : s,
        ),
      );
      toast.success(`${stop.code} ${stop.kind} · ${next}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update",
      );
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Jobs / Today" />
      <main className="relative z-10 mx-auto max-w-2xl px-4 pb-28 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Jobs / Today
        </div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          My Day
        </h1>
        <p className="mt-1 text-sm text-zinc-500" data-testid="myday-summary">
          {date} · {done} of {visible.length} stops done
        </p>

        <div className="mt-4 flex gap-2">
          {[
            [true, "My stops"],
            [false, "Whole shop"],
          ].map(([value, label]) => (
            <button
              key={label}
              data-testid={`myday-filter-${value ? "mine" : "shop"}`}
              onClick={() => setMineOnly(value)}
              className={`h-11 flex-1 rounded-md border text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                mineOnly === value
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {myTasks.length > 0 && (
          <div
            className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="myday-tasks"
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Tasks · {myTasks.filter((t) => t.done).length} of {myTasks.length}{" "}
              done
            </div>
            <div className="mt-3 space-y-2">
              {myTasks.map((t) => (
                <button
                  key={t.id}
                  data-testid={`myday-task-${t.id}`}
                  onClick={() => toggleTask(t)}
                  className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors duration-150 ${
                    t.done
                      ? "border-emerald-500/30 bg-zinc-950"
                      : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border ${
                      t.done
                        ? "border-emerald-500 bg-emerald-500 text-black"
                        : "border-zinc-600"
                    }`}
                  >
                    {t.done && <CheckCircle size={14} weight="bold" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm font-semibold ${t.done ? "text-zinc-500 line-through" : ""}`}
                    >
                      {t.title}
                    </span>
                    {(t.job_code || t.date !== date) && (
                      <span className="block text-xs text-zinc-500">
                        {t.job_code}
                        {t.date !== date
                          ? `${t.job_code ? " · " : ""}carried from ${t.date}`
                          : ""}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="myday-list">
          {visible.length === 0 && (
            <div
              data-testid="myday-empty"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            >
              <CheckCircle
                size={28}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                {mineOnly
                  ? "Nothing assigned to you today."
                  : "Nothing scheduled today."}
              </p>
            </div>
          )}
          {visible.map((s) => {
            const status = s.stop_status?.status || "Pending";
            return (
              <div
                key={`${s.job_id}-${s.kind}`}
                data-testid={`myday-stop-${s.job_id}-${s.kind}`}
                className={`rounded-lg border p-4 transition-colors duration-150 ${
                  status === "Done"
                    ? "border-emerald-500/30 bg-zinc-900/60"
                    : "border-zinc-800 bg-zinc-900"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      KIND_STYLE[s.kind]
                    }`}
                  >
                    {s.kind} · {s.time || s.window}
                  </span>
                  <Link
                    to={`/projects/${s.job_id}?tab=field`}
                    className="font-mono-data text-xs text-yellow-500 hover:underline"
                  >
                    {s.code}
                  </Link>
                  <span className="ml-auto text-xs text-zinc-500">
                    {s.crew || "Unassigned"}
                  </span>
                </div>

                <div className="mt-2 text-lg font-bold leading-tight">
                  {s.project_name}
                </div>
                {(s.areas?.length > 0 || s.label) && (
                  <div
                    className="mt-0.5 text-sm font-semibold uppercase tracking-widest text-indigo-300"
                    data-testid={`myday-areas-${s.job_id}-${s.kind}`}
                  >
                    {s.areas?.length > 0 ? s.areas.join(" · ") : s.label}
                  </div>
                )}
                <div className="text-base text-zinc-300">
                  {s.customer_name || "—"}
                </div>
                {s.customer_phone && (
                  <a
                    href={`tel:${s.customer_phone}`}
                    data-testid={`myday-phone-${s.job_id}`}
                    className="font-mono-data mt-1 inline-block text-sm text-yellow-400"
                  >
                    {s.customer_phone}
                  </a>
                )}
                {s.address && (
                  <div className="mt-1 inline-flex items-center gap-1 text-base font-medium text-zinc-200">
                    <MapPin size={16} />
                    <span className="text-xs uppercase tracking-widest text-zinc-500">
                      Job Address
                    </span>{" "}
                    {s.address}
                  </div>
                )}
                {s.summary && (
                  <p className="mt-2 text-sm text-zinc-400">{s.summary}</p>
                )}
                {(s.sink_location || s.sink_notes) && (
                  <p className="mt-1 text-xs text-zinc-500">
                    Sink: {s.sink_location || "—"}
                    {s.sink_notes ? ` · ${s.sink_notes}` : ""}
                  </p>
                )}
                {(s.material_links || []).length > 0 && (
                  <p className="mt-1 text-xs text-zinc-500">
                    Material:{" "}
                    {s.material_links
                      .map((l) => `${l.code} ${l.label}`)
                      .join(", ")}
                  </p>
                )}

                <div className="mt-4 flex gap-2">
                  <Button
                    data-testid={`myday-toggle-${s.job_id}-${s.kind}`}
                    variant="outline"
                    onClick={() => cycle(s)}
                    className={`h-12 flex-1 border font-bold uppercase tracking-wider hover:opacity-90 ${STYLE[status]}`}
                  >
                    {status === "Done" ? (
                      <>
                        <CheckCircle size={18} weight="bold" className="mr-2" />{" "}
                        Done
                      </>
                    ) : (
                      `Mark ${NEXT[status]}`
                    )}
                  </Button>
                  {s.address && (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(s.address)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`myday-directions-${s.job_id}-${s.kind}`}
                      className="flex h-12 w-12 items-center justify-center rounded-md border border-zinc-700 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                    >
                      <NavigationArrow size={18} weight="bold" />
                    </a>
                  )}
                  <Link
                    to={`/projects/${s.job_id}?tab=field`}
                    data-testid={`myday-open-${s.job_id}-${s.kind}`}
                    className="flex h-12 w-12 items-center justify-center rounded-md border border-zinc-700 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                  >
                    <ArrowRight size={18} weight="bold" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
